'use client'

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { DependencyList, EffectCallback } from 'react'
import { UseQueryResult } from '@tanstack/react-query'
import { TResponse } from 'core/service/hcResponse'
import { AxiosResponse } from 'core/utils/hcAxios'

import { useHCRouter } from '@/components/HCNextLink'
import { useAuthenticate } from 'core/components/Authenticate'
import { removeUndefinedAndNullAndEmpty } from '@/lib/utils'

export function usePrev<T>(value: T): T | undefined {
  const ref = useRef<T>()

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

export function useIsFirstRender(): boolean {
  const isFirst = useRef(true)

  if (isFirst.current) {
    isFirst.current = false

    return true
  }

  return isFirst.current
}
export function useUpdateEffect(effect: EffectCallback, deps?: DependencyList) {
  const isFirst = useIsFirstRender()

  useEffect(() => {
    if (!isFirst) {
      return effect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

type QueryStringType = Record<string, string | undefined | null>

export function useQueryString<T extends QueryStringType>(): {
  setQueryString: (
    newSearchParams: Partial<T> | ((prevState: Partial<T>) => Partial<T>),
    options?: { isRefresh?: boolean; isRedirect?: boolean; isClear?: boolean }
  ) => void
  reset: () => void
  queryString: Partial<T>
}
export function useQueryString<T extends QueryStringType>(
  defaultValue: T
): {
  setQueryString: (
    newSearchParams: Partial<T> | ((prevState: T) => Partial<T>),
    options?: { isRefresh?: boolean; isRedirect?: boolean; isClear?: boolean }
  ) => void
  reset: () => void
  queryString: T
}
export function useQueryString<T extends QueryStringType>(defaultValue?: Partial<T>) {
  const router = useHCRouter()
  const [queryString, setQueryStringState] = useState<T | Partial<T>>(
    defaultValue ? (defaultValue as T) : ({} as Partial<T>)
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const params = new URLSearchParams(window.location.search)
    const initialSearchParams = {} as T

    for (const [key, value] of params.entries()) {
      initialSearchParams[key as keyof T] = value as unknown as T[keyof T]
    }

    setQueryStringState((prev) => ({ ...prev, ...initialSearchParams }))
  }, [])

  const createNavigateUrl = useCallback((query: Partial<T>) => {
    const newSearchParams = new URLSearchParams()

    for (const key in query) {
      if (Object.prototype.hasOwnProperty.call(query, key)) {
        const value = query[key]
        if (value != null && value !== '') {
          newSearchParams.set(key, value as string)
        }
      }
    }

    if (typeof window === 'undefined') {
      return newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''
    }

    const pathname = window.location.pathname
    const queryString = newSearchParams.toString()
    return `${pathname}${queryString ? `?${queryString}` : ''}`
  }, [])

  const setQueryString = useCallback(
    (
      newSearchParams: Partial<T> | ((prevState: typeof queryString) => Partial<T>),
      options?: {
        isRefresh?: boolean
        isRedirect?: boolean
        isClear?: boolean
      }
    ) => {
      if (typeof window === 'undefined') return

      const { isRefresh = false, isRedirect = false, isClear = false } = options || {}

      let updatedSearchParams: typeof queryString

      setQueryStringState((prevQueryString) => {
        updatedSearchParams =
          typeof newSearchParams === 'function'
            ? { ...prevQueryString, ...newSearchParams(prevQueryString) }
            : { ...prevQueryString, ...newSearchParams }

        const searchParams = isClear ? removeUndefinedAndNullAndEmpty(updatedSearchParams) : updatedSearchParams
        return searchParams
      })

      const navigateUrl = createNavigateUrl(
        typeof newSearchParams === 'function'
          ? { ...queryString, ...newSearchParams(queryString) }
          : { ...queryString, ...newSearchParams }
      )

      if (isRedirect) {
        window.history.replaceState(null, '', navigateUrl)
      }
      if (isRefresh) router.refresh()
    },
    [queryString, createNavigateUrl, router]
  )

  const reset = useCallback(() => {
    if (typeof window === 'undefined') return

    const navigateUrl = createNavigateUrl({} as T)
    router.push(navigateUrl)
    setQueryStringState({} as T)
  }, [createNavigateUrl, router])

  return { setQueryString, reset, queryString }
}

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay || 500)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

export const useCurrentUrl = () => {
  const [currentUrl, setCurrentUrl] = useState('')

  useEffect(() => {
    if (typeof window === 'undefined') return
    setCurrentUrl(decodeURIComponent(window.location.href))
  }, [])
  return currentUrl
}

export const useInput = (initialValue: string) => {
  const [value, setValue] = useState(initialValue)
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }

  return {
    inputProps: {
      value,
      onChange,
    },
    value,
    setValue,
    handleChange: onChange,
  }
}

type QueryResult<TData> = UseQueryResult<AxiosResponse<TResponse<TData>>, Error>

export function useDataFromQuery<TData>(
  useQueryHook: QueryResult<TData>,
  defaultValue: TData
): { data: TData } & Omit<QueryResult<TData>, 'data'>

export function useDataFromQuery<TData, TResult>(
  useQueryHook: QueryResult<TData>,
  defaultValue: TResult,
  dataSelector: (data: TData) => TResult,
  deps?: DependencyList
): { data: TResult } & Omit<QueryResult<TData>, 'data'>

/**
 * A custom hook that processes data from a query result, allowing for optional
 * transformation of the data using a selector function.
 *
 * @template TData - The type of the data returned by the query.
 * @template TResult - The type of the transformed data if a selector is provided.
 *
 * @param useQueryHook - The query result object returned by a query hook.
 * @param defaultValue - The default value to return if the query data is not available.
 * @param dataSelector - An optional function to transform the query data into a different format.
 * @param deps - An optional dependency list that will trigger recalculation of the processed data when changed.
 *
 * @returns An object containing the processed data and the rest of the query result properties,
 *          excluding the original `data` property.
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useDataFromQuery(
 *   useSomeQuery(),
 *   [],
 *   (data) => data.items,
 *   [someState]  // This will cause data to be recalculated when someState changes
 * );
 * ```
 */
export function useDataFromQuery<TData, TResult>(
  useQueryHook: QueryResult<TData>,
  defaultValue: TData | TResult,
  dataSelector?: (data: TData) => TResult,
  deps?: DependencyList
): { data: TData | TResult } & Omit<QueryResult<TData>, 'data'> {
  const { data: queryData, ...rest } = useQueryHook

  const actualDataSelector = typeof dataSelector === 'function' ? dataSelector : undefined
  const actualDeps = Array.isArray(dataSelector) && !actualDataSelector ? dataSelector : deps

  const processedData = useMemo(() => {
    if (!queryData) return defaultValue

    try {
      if (!actualDataSelector) {
        return queryData.data.data as TData
      }
      return actualDataSelector(queryData.data.data)
    } catch (e) {
      console.error('Error processing data:', e)
      return defaultValue
    }
  }, [queryData, actualDataSelector, defaultValue, ...(actualDeps || [])])

  return { data: processedData, ...rest }
}

export function useLocalStorage<T>({
  key,
  defaultValue,
  defaultLocalStrogeValue,
}: {
  key: string
  defaultValue?: T
  defaultLocalStrogeValue?: T
}) {
  const [value, setValue] = useState<T | undefined>(defaultValue)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const item = window.localStorage.getItem(key)
      if (!item) {
        if (defaultLocalStrogeValue !== undefined) {
          setValue(defaultLocalStrogeValue)
          return
        }
        return
      }
      setValue(JSON.parse(item) as T)
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
    }
  }, [key, defaultLocalStrogeValue])

  const setStoredValue = useCallback(
    (newValue: T | ((val: T | undefined) => T)) => {
      try {
        const valueToStore = newValue instanceof Function ? newValue(value) : newValue
        setValue(valueToStore)

        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore))
        }
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error)
      }
    },
    [key, value]
  )

  const removeValue = useCallback(() => {
    try {
      setValue(undefined)
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key)
      }
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error)
    }
  }, [key])

  return { value, setValue: setStoredValue, removeValue }
}

export const useRedirectIfNotLoggedIn = () => {
  const { loginData } = useAuthenticate()
  const router = useHCRouter()

  useUpdateEffect(() => {
    if (!loginData) {
      const timer = setTimeout(() => {
        if (!loginData) {
          const currentUrl = window.location.pathname + window.location.search
          router.push(`/login?redirect=${encodeURIComponent(currentUrl)}`)
        }
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [loginData, router])

  return loginData
}
